import { QuizSession } from '../types/analytics'

export interface UserProfile {
  primaryConcerns: string[]
  physicalChallenges: string[]
  sleepIssue: boolean
  movementIssue: boolean
  nutritionIssue: boolean
  stressIssue: boolean
  duration: string
  severity: string
  communitySupport: boolean
  isCrisis: boolean
}

export const analyzeAnswers = (session: QuizSession | null): UserProfile => {
  if (!session) {
    return getDefaultProfile()
  }

  const answers = session.userAnswers
  const isCaregiver = session.quizType === 'caregiver'

  if (isCaregiver) {
    // Caregiver-specific analysis
    // Caregivers typically struggle with: stress, sleep, burnout, isolation
    const burnoutConcerns = extractArray(answers[10]) // page5_what_suffered
    const coping = extractArray(answers[16]) // page9_what_tried

    return {
      primaryConcerns: ['caregiver_burnout', 'caregiver_isolation', 'caregiver_stress'],
      physicalChallenges: burnoutConcerns,
      sleepIssue: true, // Caregivers almost always struggle with sleep
      movementIssue: false,
      nutritionIssue: false,
      stressIssue: true, // High stress for caregivers
      duration: 'ongoing',
      severity: 'high',
      communitySupport: coping.includes('support_groups'),
      isCrisis: false,
    }
  }

  // Patient-specific analysis
  const primaryConcerns = extractArray(answers[4]) // page3_primary_concern
  const physicalChallenges = extractArray(answers[6]) // page5_physical_recovery
  const duration = extractString(answers[8]) // page7_time_since_treatment
  const severity = extractString(answers[10]) // page9_severity_scale

  // Extract holistic questions (pageNum: 9, 10, 11, 12)
  const sleepQuality = extractString(answers[9]) // page7_holistic_sleep
  const movement = extractString(answers[10]) // page8_holistic_movement
  const nutrition = extractString(answers[11]) // page9_holistic_nutrition
  const stressManagement = extractString(answers[12]) // page10_holistic_stress

  // Community support (from self-care tools)
  const selfCareTools = extractArray(answers[12]) // page11_self_care_tools
  const communitySupport = selfCareTools.includes('support_groups') ||
                          selfCareTools.includes('journaling')

  const isCrisis = severity === 'crisis'

  return {
    primaryConcerns,
    physicalChallenges,
    sleepIssue: isSleepPoor(sleepQuality),
    movementIssue: isMovementLow(movement),
    nutritionIssue: isNutritionPoor(nutrition),
    stressIssue: isStressHigh(stressManagement),
    duration,
    severity,
    communitySupport,
    isCrisis,
  }
}

const extractArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string') return [value]
  return []
}

const extractString = (value: unknown): string => {
  return typeof value === 'string' ? value : ''
}

const isSleepPoor = (quality: string): boolean => {
  return ['poor_sleep', 'no_sleep', 'moderate_sleep'].includes(quality)
}

const isMovementLow = (movement: string): boolean => {
  return ['sedentary', 'no_movement', 'light_activity'].includes(movement)
}

const isNutritionPoor = (nutrition: string): boolean => {
  return ['inconsistent', 'nausea', 'food_anxiety'].includes(nutrition)
}

const isStressHigh = (stress: string): boolean => {
  return ['no_routine', 'exhausted', 'partial_routine'].includes(stress)
}

const getDefaultProfile = (): UserProfile => ({
  primaryConcerns: [],
  physicalChallenges: [],
  sleepIssue: false,
  movementIssue: false,
  nutritionIssue: false,
  stressIssue: false,
  duration: '',
  severity: '',
  communitySupport: false,
  isCrisis: false,
})

export const generatePersonalizedSummary = (profile: UserProfile): string => {
  if (profile.isCrisis) {
    return `You're in crisis right now. Your responses show you're overwhelmed, isolated, and struggling to function. This is urgent, and you need support now.`
  }

  const symptoms: string[] = []

  if (profile.sleepIssue) symptoms.push('your sleep is disrupted')
  if (profile.primaryConcerns.includes('anxiety')) symptoms.push('anxiety is constant')
  if (profile.primaryConcerns.includes('depression')) symptoms.push('depression is deep')
  if (profile.movementIssue) symptoms.push('your body feels stuck')
  if (profile.nutritionIssue) symptoms.push('you\'re skipping meals and forgetting to drink water')
  if (profile.stressIssue) symptoms.push('stress and chaos are relentless')
  if (profile.primaryConcerns.includes('isolation')) symptoms.push('you feel alone')

  if (symptoms.length === 0) {
    return `Your recovery is progressing. There are still areas to work on, but you're moving in the right direction.`
  }

  const symptomText = symptoms.slice(0, 4).join(', ')

  return `Based on what you've shared: Your nervous system is still in protection mode. Right now ${symptomText}. These aren't separate problems—they're all connected and rooted in what your body learned during cancer.`
}

export const generatePersonalizedHope = (profile: UserProfile): string => {
  if (profile.isCrisis) {
    return `You don't have to feel this desperate anymore. Real help exists.`
  }

  if (profile.sleepIssue && profile.stressIssue) {
    return `When your nervous system learns it's safe again—when you finally sleep through the night and the constant worry lifts—everything changes.`
  }

  if (profile.movementIssue) {
    return `You'll move like yourself again. Not trapped in a body that feels like a stranger.`
  }

  return `The anxiety will calm. The fog will lift. You'll reconnect with yourself and the people you love.`
}
